import * as React from 'react';
import { useAuthStore } from '@/auth';
import { Studysession } from '@/shared/i18n';

import { getStudySessionsOptions } from '../services/get-study-sessions';
import { StudySessions } from '../types/study-sessions';
import { getProfileStudySessions } from '../services/get-profile-study-sessions';
import { update } from '../services/update-profile-study-sessions';
import { useStudySessionStore } from '../stores/study-session-store';
import { ActionFeedbackType } from '../../ui/shared/module-actions-feedback';
import { isRight, isLeft } from '@shared-commons/utils';
import { Auth } from 'firebase/auth';

export const useStudySessions = (dict: Studysession, auth: Auth) => {
  const { user } = useAuthStore();

  const {
    studySessionsOptions,
    profileStudySessions,
    setActionFeedback,
    setError,
    setIsLoading,
    setStudySessionsOptions,
    setProfileStudySessions,
    resetStore,
  } = useStudySessionStore();

  const messageMap = React.useMemo(
    () =>
      ({
        success: 'Operazione completata con successo',
        danger: "Si è verificato un errore durante l'operazione",
      }) as Partial<Record<ActionFeedbackType, string>>,
    [],
  );

  const updateStudySession = React.useCallback(
    async (studySession: StudySessions[0]) => {
      setError(null);
      setIsLoading(true);
      const accessToken = await auth.currentUser!.getIdToken(true);
      const result = await update(user?.email, [studySession], accessToken)();
      if (isRight(result)) {
        setIsLoading(false);
        setError(null);
        setActionFeedback({
          message: messageMap?.['success'],
          type: 'success',
        });
      } else if (isLeft(result)) {
        const error = new Error(
          result.value?.message || dict.sessions.validation_update_study_sessions,
        );
        setError(error);
        setIsLoading(false);
        setActionFeedback({
          message: messageMap?.['danger'],
          type: 'danger',
        });
      }
    },
    [user?.email, messageMap, setActionFeedback, setError, setIsLoading, dict, auth],
  );

  const refreshStudySessionsOptions = React.useCallback(async () => {
    setError(null);
    const accessToken = await auth.currentUser!.getIdToken(true);
    const result = await getStudySessionsOptions(accessToken)();
    if (isRight(result)) {
      setStudySessionsOptions(result.value);
    } else if (isLeft(result)) {
      const error = new Error(
        result.value?.message || dict.sessions.validation_refresh_study_session_options,
      );
      setError(error);
      setIsLoading(false);
      setActionFeedback({
        message: messageMap?.['danger'],
        type: 'danger',
      });
    }
  }, [messageMap, setActionFeedback, setError, setIsLoading, setStudySessionsOptions, dict, auth]);

  const refreshProfileStudySessions = React.useCallback(async () => {
    setError(null);
    const accessToken = await auth.currentUser!.getIdToken(true);
    const result = await getProfileStudySessions(user?.email, accessToken)();
    if (isRight(result)) {
      const output = result.value[0] || {};
      setProfileStudySessions(output);
    } else if (isLeft(result)) {
      const error = new Error(
        result.value?.message || dict.sessions.validation_refresh_study_sessions,
      );
      setError(error);
      setIsLoading(false);
      setActionFeedback({
        message: messageMap?.['danger'],
        type: 'danger',
      });
    }
  }, [
    user?.email,
    messageMap,
    setActionFeedback,
    setError,
    setIsLoading,
    setProfileStudySessions,
    dict,
    auth,
  ]);

  return {
    studySessionsOptions,
    profileStudySessions,
    refreshStudySessionsOptions,
    updateStudySession,
    refreshProfileStudySessions,
    resetStore,
  };
};
